import { age } from '../../../src/redux/selectors/patientSelector';
import { defineMessages, useIntl } from 'react-intl';
import { makeStyles } from '@lifeomic/chroma-react/styles';
import { Subject } from '@lifeomic/phc-web-toolkit/src/common/types/Subject';
import { Text } from '@lifeomic/chroma-react/components/Text';
import * as React from 'react';
import clsx from 'clsx';

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    flexFlow: 'wrap',
  },
  text: {
    marginRight: theme.spacing(1.5),
    whiteSpace: 'nowrap',
    '&:last-of-type': {
      marginRight: 0,
    },
  },
  label: {
    fontWeight: theme.typography.fontWeightBold,
    marginRight: theme.spacing(0.5),
  },
  value: {
    display: 'inline-block',
    '&::first-letter': {
      textTransform: 'capitalize',
    },
    '&:empty': {
      '&:after': {
        content: "'—'",
      },
    },
  },
}));

const messages = defineMessages({
  livingStatusLabel: {
    id: 'pages.omicsExplorer.patientHeader.fieldLabels.livingStatusLabel',
    defaultMessage: 'Living Status:',
  },
  raceLabel: {
    id: 'pages.omicsExplorer.patientHeader.fieldLabels.raceLabel',
    defaultMessage: 'Race:',
  },
  ethnicityLabel: {
    id: 'pages.omicsExplorer.patientHeader.fieldLabels.ethnicityLabel',
    defaultMessage: 'Ethnicity:',
  },
  DOBLabel: {
    id: 'pages.omicsExplorer.patientHeader.fieldLabels.DOBLabel',
    defaultMessage: 'DOB:',
  },
  ageLabel: {
    id: 'pages.omicsExplorer.patientHeader.fieldLabels.ageLabel',
    defaultMessage: 'Age:',
  },
  living: {
    id: 'pages.omicsExplorer.patientHeader.fieldValues.living',
    defaultMessage: 'Living',
  },
  deceased: {
    id: 'pages.omicsExplorer.patientHeader.fieldValues.deceased',
    defaultMessage: 'Deceased',
  },
  gender: {
    id: 'pages.omicsExplorer.patientHeader.fieldValues.gender',
    defaultMessage: 'Gender: ',
  },
});

interface DetailsProps {
  className?: string;
  isMaskedModeEnabled?: boolean;
  subject?: Subject;
}

const generateContent = (subject: Subject, mask: any, formatMessage: any) => {
  return [
    {
      label: formatMessage(messages.livingStatusLabel),
      value: !subject
        ? null
        : subject.isDeceased
        ? formatMessage(messages.deceased)
        : formatMessage(messages.living),
    },
    {
      label: formatMessage(messages.raceLabel),
      value: subject?.race,
    },
    {
      label: formatMessage(messages.ethnicityLabel),
      value: subject?.ethnicity,
    },
    {
      label: formatMessage(messages.DOBLabel),
      value: subject?.birthDateDisplayValue,
    },
    {
      label: formatMessage(messages.ageLabel),
      value: !subject ? null : age(subject as fhir.Patient, mask),
    },
    {
      label: formatMessage(messages.gender),
      value: subject?.gender,
    },
  ];
};

export const SubjectDetails: React.FC<DetailsProps> = ({
  className,
  isMaskedModeEnabled,
  subject,
}) => {
  const classes = useStyles({});
  const { formatMessage } = useIntl();

  return (
    <div className={clsx(classes.root, className)}>
      {/* @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks. */}
      {generateContent(subject, isMaskedModeEnabled, formatMessage).map(
        (content, i) => (
          <Text key={i} size="subbody" className={classes.text}>
            <span className={classes.label}>{content.label}</span>
            <span className={classes.value}>{content.value}</span>
          </Text>
        ),
      )}
    </div>
  );
};
